import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type CSSProperties,
  type ReactNode,
} from 'react';

// ─── Theme ───────────────────────────────────────────────────────────────────

const THEME = {
  bg: '#fffcf7',
  border: '#e7e3db',
  text: '#1c1917',
  textMuted: '#78716c',
  accent: '#ea580c',
  accentBg: '#fff7ed',
  accentBorder: '#fed7aa',
  danger: '#b91c1c',
  dangerBg: '#fef2f2',
  dangerBorder: '#fecaca',
  divider: '#e7e3db',
  shadow: '0 8px 32px rgba(0,0,0,0.15)',
  radius: 16,
  itemRadius: 10,
  minWidth: 220,
};

// ─── Context ─────────────────────────────────────────────────────────────────

type CtxContextValue = {
  onDismiss: () => void;
};

const CtxContext = createContext<CtxContextValue>({ onDismiss: () => {} });

// ─── ContextMenu (container) ─────────────────────────────────────────────────

export type ContextMenuProps = {
  /** X coordinate (screen pixels) */
  x: number;
  /** Y coordinate (screen pixels) */
  y: number;
  /** Called when the menu should close */
  onDismiss: () => void;
  /** Optional minimum width (default 220) */
  minWidth?: number;
  children: ReactNode;
  /** Optional className for extra styling */
  className?: string;
};

/**
 * Context menu container con posizionamento dinamico.
 * Se il punto (x,y) è nella metà superiore del viewport → menu appare sotto.
 * Se è nella metà inferiore → menu appare sopra.
 * Chiude con click fuori o tasto Escape.
 */
export function ContextMenu({ x, y, onDismiss, minWidth, className, children }: ContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Posizionamento dinamico
  const viewportH = typeof window !== 'undefined' ? window.innerHeight : 800;
  const isTopHalf = y < viewportH / 2;

  const containerStyle: CSSProperties = {
    position: 'fixed',
    left: x,
    ...(isTopHalf ? { top: y + 12 } : { bottom: viewportH - y + 12 }),
    transform: 'translateX(-50%)',
    zIndex: 5000,
    minWidth: minWidth ?? THEME.minWidth,
    background: THEME.bg,
    borderRadius: THEME.radius,
    boxShadow: THEME.shadow,
    border: `1px solid ${THEME.border}`,
    padding: 8,
  };

  // Chiudi con Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  // Chiudi con click fuori
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onDismiss();
      }
    };
    window.addEventListener('pointerdown', onPointerDown, { capture: true });
    return () => window.removeEventListener('pointerdown', onPointerDown, { capture: true });
  }, [onDismiss]);

  return (
    <CtxContext.Provider value={{ onDismiss }}>
      <div
        ref={ref}
        role="menu"
        className={className}
        style={containerStyle}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </CtxContext.Provider>
  );
}

// ─── ContextMenuItem ─────────────────────────────────────────────────────────

export type ContextMenuItemProps = {
  icon?: ReactNode;
  label: string;
  onClick: () => void;
  /** Use 'danger' for destructive actions like delete */
  variant?: 'default' | 'danger';
  /** Keyboard shortcut hint (e.g. "Del", "⌫") */
  shortcut?: string;
  /** Disable the item */
  disabled?: boolean;
};

export function ContextMenuItem({
  icon,
  label,
  onClick,
  variant = 'default',
  shortcut,
  disabled = false,
}: ContextMenuItemProps) {
  const { onDismiss } = useContext(CtxContext);

  const isDanger = variant === 'danger';
  const color = isDanger ? THEME.danger : THEME.text;
  const hoverBg = isDanger ? THEME.dangerBg : THEME.accentBg;

  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={() => {
        onClick();
        onDismiss();
      }}
      style={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        border: 'none',
        background: 'transparent',
        borderRadius: THEME.itemRadius,
        cursor: disabled ? 'not-allowed' : 'pointer',
        textAlign: 'left',
        fontSize: 15,
        fontFamily: 'Caveat, cursive',
        color: disabled ? '#a8a29e' : color,
        opacity: disabled ? 0.5 : 1,
        transition: 'background 0.12s',
      }}
      onMouseEnter={(e) => {
        if (!disabled) (e.currentTarget as HTMLElement).style.background = hoverBg;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'transparent';
      }}
    >
      {icon && <span style={{ fontSize: 18, flexShrink: 0, width: 24, textAlign: 'center' }}>{icon}</span>}
      <span className="flex-1">{label}</span>
      {shortcut && (
        <span style={{ fontSize: 12, color: THEME.textMuted, fontFamily: 'monospace' }}>{shortcut}</span>
      )}
    </button>
  );
}

// ─── ContextMenuDivider ──────────────────────────────────────────────────────

export function ContextMenuDivider() {
  return <div style={{ height: 1, background: THEME.divider, margin: '4px 4px' }} />;
}

// ─── ContextMenuSection ──────────────────────────────────────────────────────

export type ContextMenuSectionProps = {
  label: string;
  children: ReactNode;
};

/**
 * Gruppo di elementi con etichetta (es. "Colore", "Spessore").
 */
export function ContextMenuSection({ label, children }: ContextMenuSectionProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <p
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: THEME.textMuted,
          margin: '4px 4px 2px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {label}
      </p>
      {children}
    </div>
  );
}

// ─── ContextMenuSlider ───────────────────────────────────────────────────────

export type ContextMenuSliderProps = {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  /** Show current value as label */
  showValue?: boolean;
  /** Unit suffix (e.g. "px") */
  unit?: string;
};

/**
 * Slider accessibile: funziona con mouse, touch e tastiera (frecce).
 */
export function ContextMenuSlider({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
  showValue = true,
  unit = '',
}: ContextMenuSliderProps) {
  return (
    <div style={{ padding: '4px 4px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 13, color: THEME.textMuted, fontFamily: 'Caveat, cursive' }}>{label}</span>
        {showValue && (
          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: THEME.accent,
              fontFamily: 'Caveat, cursive',
            }}
          >
            {value}
            {unit}
          </span>
        )}
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        onKeyDown={(e) => {
          // Frecce per accessibilità tastiera
          if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
            e.preventDefault();
            onChange(Math.min(max, value + step));
          }
          if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
            e.preventDefault();
            onChange(Math.max(min, value - step));
          }
        }}
        style={{
          width: '100%',
          height: 6,
          borderRadius: 3,
          appearance: 'auto',
          accentColor: THEME.accent,
          cursor: 'pointer',
        }}
        aria-label={label}
      />
    </div>
  );
}

// ─── ContextMenuSegmented ────────────────────────────────────────────────────

export type ContextMenuSegmentedOption = {
  value: string;
  label: string;
  icon?: ReactNode;
};

export type ContextMenuSegmentedProps = {
  label: string;
  options: ContextMenuSegmentedOption[];
  value: string;
  onChange: (value: string) => void;
};

/**
 * Selezione a segmenti (tipo toggle group) per pattern, terminatori, etc.
 */
export function ContextMenuSegmented({ label, options, value, onChange }: ContextMenuSegmentedProps) {
  return (
    <div style={{ padding: '4px 4px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 13, color: THEME.textMuted, fontFamily: 'Caveat, cursive' }}>{label}</span>
      <div
        style={{
          display: 'flex',
          gap: 2,
          background: '#faf7f2',
          borderRadius: 8,
          padding: 2,
        }}
      >
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                padding: '6px 8px',
                border: 'none',
                borderRadius: 6,
                background: active ? 'white' : 'transparent',
                boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontFamily: 'Caveat, cursive',
                color: active ? THEME.accent : THEME.textMuted,
                fontWeight: active ? 700 : 500,
                transition: 'all 0.12s',
                minWidth: 0,
              }}
              title={opt.label}
              aria-label={opt.label}
            >
              {opt.icon && <span style={{ fontSize: 16 }}>{opt.icon}</span>}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {opt.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── ContextMenuNumberInput ──────────────────────────────────────────────────

export type ContextMenuNumberInputProps = {
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  onChange: (value: number) => void;
};

/**
 * Campo numerico editabile (input type=number) per il menu contestuale, con
 * min/max/step e unità. Stesso stile di ContextMenuSlider (Caveat, accent).
 */
export function ContextMenuNumberInput({
  label,
  value,
  min,
  max,
  step = 1,
  unit = '',
  onChange,
}: ContextMenuNumberInputProps) {
  const commit = (raw: string) => {
    if (raw.trim() === '') return; // campo svuotato: niente onChange (evita commit 0 a metà digitazione)
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    let v = n;
    if (min !== undefined) v = Math.max(min, v);
    if (max !== undefined) v = Math.min(max, v);
    onChange(v);
  };
  return (
    <div style={{ padding: '4px 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
      <span
        style={{
          flex: 1,
          fontSize: 13,
          color: THEME.textMuted,
          fontFamily: 'Caveat, cursive',
        }}
      >
        {label}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={Number.isFinite(value) ? value : 0}
          onChange={(e) => commit(e.target.value)}
          aria-label={label}
          style={{
            width: 64,
            padding: '6px 8px',
            border: `1px solid ${THEME.border}`,
            borderRadius: 8,
            background: 'white',
            fontSize: 14,
            fontWeight: 700,
            color: THEME.accent,
            fontFamily: 'Caveat, cursive',
            textAlign: 'center',
          }}
        />
        {unit && (
          <span style={{ fontSize: 12, color: THEME.textMuted, fontFamily: 'Caveat, cursive' }}>
            {unit}
          </span>
        )}
      </span>
    </div>
  );
}
